import { CircularLoader } from '@modules/common/components/CircularLoader';
import { useIsCollectionsEnabled } from '@modules/common/hooks';
import { trackSaveFeedbackEvent } from '@modules/savedFeedbackList/components/utils';
import { useIsSnippetOrFeedbackSaved } from '@modules/savedFeedbackList/hooks';
import { SavedFeedbackListActionType } from '@modules/savedFeedbackList/store/savedFeedbackListActionType';
import { useSavedFeedbackListStore } from '@modules/savedFeedbackList/store/useSavedFeedbackListStore';
import BookmarkBorderRoundedIcon from '@mui/icons-material/BookmarkBorderRounded';
import BookmarkRoundedIcon from '@mui/icons-material/BookmarkRounded';
import { IconButton, Stack } from '@mui/material';
import { useGetUser } from 'v2/core/services/user';
import { shallow } from 'zustand/shallow';
import { AddToCollectionIconButton } from './AddToCollectionIconButton';
import CopyFeedbackURLToClipboard from './CopyFeedbackURLToClipboard';
import { RemoveFromCollectionIconButton } from './RemoveFromCollectionIconButton';
import { SaveFeedbackContext } from './SaveFeedbackContext';

interface SaveFeedbackProps {
  iconColor?: string;
  feedbackID: string;
  snippetID?: string;
  labelID?: string;
  getSnippetUrl?: () => string;
  shouldShowRemoveFromCollectionButton?: boolean;
  summaryIndex?: number;
}

export const SaveFeedback = ({
  iconColor,
  feedbackID,
  snippetID = '',
  labelID = '',
  getSnippetUrl,
  shouldShowRemoveFromCollectionButton = false,
  summaryIndex = 0,
}: SaveFeedbackProps) => {
  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();
  const { dispatch, saveItem } = useSavedFeedbackListStore(
    ({ dispatch, visual: { saveItem } }) => ({
      dispatch,
      saveItem,
    }),
    shallow,
  );

  const isCollectionEnabled = useIsCollectionsEnabled();

  const isSavedItem = useIsSnippetOrFeedbackSaved({
    feedbackID,
    snippetID,
    labelID,
  });

  if (isCollectionEnabled) {
    return (
      <SaveFeedbackContext.Provider
        value={{ feedbackID, labelID, summaryIndex, snippetID }}
      >
        <Stack direction="row" spacing={0.5}>
          {shouldShowRemoveFromCollectionButton && (
            <RemoveFromCollectionIconButton
              feedbackID={feedbackID}
              iconColor={iconColor}
            />
          )}
          {!snippetID && (
            <CopyFeedbackURLToClipboard
              isDetailView={false}
              feedbackID={feedbackID}
            />
          )}
          <AddToCollectionIconButton
            feedbackID={feedbackID}
            iconColor={iconColor}
          />
        </Stack>
      </SaveFeedbackContext.Provider>
    );
  }

  const onToggleSave = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    const feedbackLink = `${window.location.origin}/${tenantSlug}/record/${feedbackID}`;

    dispatch({
      type: SavedFeedbackListActionType.SET_VISUAL,
      payload: {
        saveItem: {
          isLoading: true,
          feedbackID,
          snippetID,
          action: isSavedItem ? 'UNSAVE' : 'SAVE',
        },
      },
    });

    if (!snippetID) {
      dispatch({
        type: isSavedItem
          ? SavedFeedbackListActionType.UNSAVE_FEEDBACK
          : SavedFeedbackListActionType.SAVE_FEEDBACK,
        payload: { feedbackID },
      });
      trackSaveFeedbackEvent({
        eventName: `Save Feedback - Feedback Record ${
          isSavedItem ? 'Unsaved' : 'Saved'
        }`,
        feedbackLink,
      });
      return;
    }

    dispatch({
      type: isSavedItem
        ? SavedFeedbackListActionType.UNSAVE_SNIPPET
        : SavedFeedbackListActionType.SAVE_SNIPPET,
      payload: {
        feedbackID,
        snippetID,
        labelID,
      },
    });
    trackSaveFeedbackEvent({
      eventName: `Save Feedback - Summary Snippet ${
        isSavedItem ? 'Unsaved' : 'Saved'
      }`,
      feedbackLink,
      snippetLink: getSnippetUrl?.(),
    });
  };

  if (
    saveItem.isLoading &&
    saveItem.feedbackID === feedbackID &&
    saveItem.snippetID === snippetID
  ) {
    return <CircularLoader />;
  }

  return (
    <IconButton onClick={onToggleSave} disabled={saveItem.isLoading}>
      {isSavedItem ? (
        <BookmarkRoundedIcon
          sx={{
            color: 'warning.main',
          }}
        />
      ) : (
        <BookmarkBorderRoundedIcon
          sx={{
            color: iconColor || 'primary.main',
          }}
        />
      )}
    </IconButton>
  );
};
